// Admin API client — /admin/users/* (mirrors the API's users aggregate).

import type {
  AssignableAdminRole,
  AdminLifecycleResponse,
  AdminSuperadminUser,
  AdminUserSummaryItem,
  AdminUserDetail,
  LifecyclePeriodId,
} from '@a7y/types';
import { client, PaginatedResponse } from './transport';

export const usersApi = {
  listSuperadmins: () => client.get<{ data: AdminSuperadminUser[] }>(`/users/superadmins`),
  // Users
  getUsers: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    periodId?: string;
    includeApptonomy?: boolean;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page) searchParams.set('offset', String((params.page - 1) * (params.limit || 20)));
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params?.periodId) searchParams.set('periodId', params.periodId);
    if (params?.includeApptonomy) searchParams.set('includeApptonomy', 'true');
    return client.get<PaginatedResponse<AdminUserSummaryItem>>(`/users?${searchParams}`);
  },
  getUserDetail: (id: string) => client.get<AdminUserDetail>(`/users/${id}`),
  removeUserFromOrg: (userId: string, membershipId: string, reason: string) =>
    client.post<{ message: string; userId: string; membershipId: string }>(
      `/users/${userId}/remove-from-org`,
      { membershipId, reason }
    ),
  changeUserRole: (userId: string, membershipId: string, role: string) =>
    client.post<{ message: string; userId: string; membershipId: string; role: string }>(
      `/users/${userId}/change-role`,
      { membershipId, role }
    ),
  suspendUser: (id: string) => client.post<void>(`/users/${id}/disable`),
  updateUserName: (userId: string, data: { firstName?: string; lastName?: string }) =>
    client.patch<{ message: string; userId: string; firstName: string; lastName: string }>(
      `/users/${userId}`,
      data
    ),
  /** A7Y-4957: assign (or clear with `null`) a staff account's limited admin-site role. */
  setUserAdminRole: (userId: string, role: AssignableAdminRole | null) =>
    client.patch<{ userId: string; adminRole: AssignableAdminRole | null }>(
      `/users/${userId}/admin-role`,
      { role }
    ),
  deleteUser: (userId: string) =>
    client.delete<{
      success: boolean;
      message: string;
      errors: string[];
      /** A7Y-4614: access revoked, shared material held — erasure not complete. */
      pending?: boolean;
    }>(`/users/${userId}`),
  bulkDeleteUsers: (emailPattern: string) =>
    client.post<{
      message: string;
      deleted: number;
      failed: number;
      /** A7Y-4614: accounts whose access was revoked but whose deletion is pending. */
      pending: number;
      results: Array<{
        userId: string;
        email: string;
        success: boolean;
        pending?: boolean;
        error?: string;
      }>;
    }>('/users/bulk-delete', { emailPattern }),
  // Lifecycle
  getUsersLifecycle: (params?: {
    periodId?: LifecyclePeriodId;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.periodId) searchParams.set('periodId', params.periodId);
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params?.limit !== undefined) searchParams.set('limit', String(params.limit));
    if (params?.offset !== undefined) searchParams.set('offset', String(params.offset));
    return client.get<AdminLifecycleResponse>(`/users/lifecycle?${searchParams}`);
  },
};
